import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { ArrowLeft, Plus, Users, MapPin, ChevronRight } from 'lucide-react';
import SponsorTile from '../components/SponsorTile';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubcategoryDetail() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [category, setCategory] = useState(null);
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sponsor, setSponsor] = useState(null);
  const [loading, setLoading] = useState(true);

  // Category colors
  const getCategoryColor = (sortOrder) => {
    const colors = ['#FF6B35', '#4ECDC4', '#FFE66D', '#95E1D3', '#A8DADC', '#DDA0DD'];
    return colors[(sortOrder - 1) % colors.length];
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get category details
        const catRes = await fetch(`${API_URL}/api/categories/${categoryId}`);
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategory(catData);
          
          // Get parent if exists
          if (catData.parent_id) {
            const parentRes = await fetch(`${API_URL}/api/categories/${catData.parent_id}`);
            if (parentRes.ok) {
              setParent(await parentRes.json());
            }
          }
          
          // Get children (level 3 leaves)
          const childRes = await fetch(`${API_URL}/api/categories/${categoryId}/children`);
          if (childRes.ok) {
            const childData = await childRes.json();
            setChildren(childData.children || []);
          }
        }
        
        // Get groups in this category
        const groupRes = await fetch(`${API_URL}/api/servers?category=${categoryId}`);
        if (groupRes.ok) {
          const groupData = await groupRes.json();
          setGroups(groupData.servers || []);
        }

        // Get category sponsor (if any)
        try {
          const sponsorRes = await fetch(`${API_URL}/api/sponsors/category/${categoryId}`);
          if (sponsorRes.ok) {
            const sponsorData = await sponsorRes.json();
            if (sponsorData.sponsor) {
              setSponsor(sponsorData.sponsor);
            }
          }
        } catch (err) {
          // Sponsor fetch failure is non-critical
          console.log('No sponsor for category');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary, #FAFAFA)' }}>
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded" style={{ backgroundColor: 'var(--fy-border-subtle, #E5E5E5)' }} />
            <div className="h-4 w-32 rounded" style={{ backgroundColor: 'var(--fy-border-subtle, #E5E5E5)' }} />
            <div className="grid grid-cols-2 gap-3 mt-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-16 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary, #F5F5F5)' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary, #FAFAFA)' }}>
        <div className="text-center">
          <p style={{ color: 'var(--fy-text-secondary, #666)' }}>Category not found</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: 'var(--fy-accent-positive, #4CAF50)', color: 'white' }}
          >
            Back to Browse
          </button>
        </div>
      </div>
    );
  }

  const color = parent ? getCategoryColor(parent.sort_order) : getCategoryColor(category.sort_order);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary, #FAFAFA)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-40"
        style={{ 
          backgroundColor: color + '20',
          borderBottom: `2px solid ${color}`
        }}
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center gap-3 h-14">
            <button
              onClick={() => navigate('/')}
              className="p-2 -ml-2 rounded-lg"
              style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}>
                {category.name}
              </h1>
              {parent && (
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary, #666)' }}>
                  {parent.name}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* SPONSOR TILE — Below header, above group listings */}
        {sponsor && (
          <div className="mb-6">
            <SponsorTile sponsor={sponsor} variant="category" />
          </div>
        )}

        {/* Level 3 children (leaves) */}
        {children.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-medium mb-3" style={{ color: 'var(--fy-text-secondary, #666)' }}>
              Explore
            </h2>
            <div className="grid grid-cols-2 gap-2">
              {children.map(child => (
                <button
                  key={child.id}
                  onClick={() => navigate(`/category/${child.id}`)}
                  className="flex items-center gap-2 p-3 rounded-xl text-left transition-all hover:scale-[1.02]"
                  style={{ 
                    backgroundColor: color + '15',
                    border: `1px solid ${color}30`
                  }}
                >
                  <span className="flex-1 text-sm font-medium" style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}>
                    {child.name}
                  </span>
                  <ChevronRight className="w-4 h-4" style={{ color }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Create button - only for logged in users */}
        {user && (
          <button
            onClick={() => navigate(`/create-server?category=${categoryId}`)}
            className="w-full flex items-center justify-center gap-2 p-4 rounded-xl mb-6 font-medium"
            style={{ backgroundColor: color, color: 'white' }}
          >
            <Plus className="w-5 h-5" />
            Create a group
          </button>
        )}

        {/* Groups list */}
        {groups.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-sm font-medium" style={{ color: 'var(--fy-text-secondary, #666)' }}>
              Groups
            </h2>
            {groups.map(group => (
              <button
                key={group.id}
                onClick={() => navigate(`/server/${group.id}`)}
                className="w-full text-left p-4 rounded-xl transition-all hover:scale-[1.01]"
                style={{ 
                  backgroundColor: 'var(--fy-background-secondary, #F5F5F5)',
                  border: '1px solid var(--fy-border-subtle, #E5E5E5)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: color + '30' }}
                  >
                    <span className="text-lg font-semibold" style={{ color }}>{group.name?.[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" style={{ color: 'var(--fy-text-primary, #1A1A1A)' }}>
                      {group.name}
                    </h3>
                    {group.description && (
                      <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--fy-text-secondary, #666)' }}>
                        {group.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--fy-text-disabled, #999)' }}>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {group.member_count || 0}
                      </span>
                      {group.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {group.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div 
            className="text-center py-12 rounded-xl"
            style={{ backgroundColor: 'var(--fy-background-secondary, #F5F5F5)' }}
          >
            <p style={{ color: 'var(--fy-text-secondary, #666)' }}>
              No groups yet in {category.name}
            </p>
            {user ? (
              <button
                onClick={() => navigate(`/create-server?category=${categoryId}`)}
                className="mt-4 px-4 py-2 rounded-lg inline-flex items-center gap-2"
                style={{ backgroundColor: color, color: 'white' }}
              >
                <Plus className="w-4 h-4" />
                Be the first
              </button>
            ) : (
              <p className="mt-2 text-sm" style={{ color: 'var(--fy-text-disabled, #999)' }}>
                Sign in to create a group
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
